/* RiddimRoom product foundation (Stage 2)
   <rr-product-info>: variant switching, gallery, lightbox, form validation.
   No libraries, no globals. Safe for multiple sections and theme-editor reloads.
   Sept 2026: gallery items can be videos; hidden videos are paused. */
if (!customElements.get('rr-product-info')) {
  class RRProductInfo extends HTMLElement {
    connectedCallback() {
      this.ac = new AbortController();
      const signal = this.ac.signal;

      this.variants = {};
      try {
        const data = this.querySelector('[data-rr-variants]');
        if (data) JSON.parse(data.textContent).forEach((v) => (this.variants[v.id] = v));
      } catch (e) {
        this.variants = {};
      }

      this.form = document.getElementById(this.dataset.formId);
      this.select = this.querySelector('[data-rr-variant-select]');
      this.lightbox = this.querySelector('[data-rr-lightbox]');

      if (this.select) {
        this.select.addEventListener('change', () => this.onVariantChange(this.select.value), { signal });
      }

      this.querySelectorAll('[data-rr-thumb]').forEach((btn) => {
        btn.addEventListener('click', () => this.showImage(btn.dataset.imageId), { signal });
      });

      this.querySelectorAll('[data-rr-zoom]').forEach((btn) => {
        btn.addEventListener('click', () => this.openLightbox(btn), { signal });
      });

      if (this.lightbox) {
        this.lightbox.addEventListener('click', (e) => {
          if (e.target === this.lightbox || e.target.closest('[data-rr-lightbox-close]')) this.lightbox.close();
        }, { signal });
        this.lightbox.addEventListener('close', () => {
          const img = this.lightbox.querySelector('[data-rr-lightbox-img]');
          if (img) img.removeAttribute('src');
        }, { signal });
      }

      /* Form uses novalidate, so validate manually (capture phase: runs before product-form.js) */
      if (this.form) {
        this.form.addEventListener('submit', (e) => {
          if (!this.form.checkValidity()) {
            e.preventDefault();
            e.stopImmediatePropagation();
            this.form.reportValidity();
          }
        }, { capture: true, signal });
      }
    }

    disconnectedCallback() {
      if (this.ac) this.ac.abort();
    }

    onVariantChange(id) {
      const v = this.variants[id];
      if (!v || !this.form) return;

      const idInput = this.form.querySelector('input[name="id"]');
      if (idInput) {
        idInput.value = id;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      const set = (sel, text) => {
        const el = this.querySelector(sel);
        if (el) el.textContent = text;
      };
      set('[data-rr-price-now]', v.price);
      set('[data-rr-price-was]', v.was);
      const wasWrap = this.querySelector('[data-rr-price-was-wrap]');
      const badge = this.querySelector('[data-rr-price-badge]');
      if (wasWrap) wasWrap.hidden = !v.sale;
      if (badge) badge.hidden = !v.sale;

      this.updateAvailability(v);
      if (v.image) this.showImage(String(v.image));
      this.updateQuantity(v);

      if (this.dataset.updateUrl === 'true' && !(window.Shopify && window.Shopify.designMode)) {
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', id);
          window.history.replaceState({}, '', url.toString());
        } catch (e) {}
      }
    }

    updateAvailability(v) {
      const strings = window.variantStrings || {};
      const productForm = this.form.closest('product-form') || this.querySelector('product-form');
      const submit = this.form.querySelector('[type="submit"]');
      const soldOut = strings.soldOut || 'Sold out';
      const addText = strings.addToCart || null;

      if (productForm && typeof productForm.toggleSubmitButton === 'function') {
        productForm.toggleSubmitButton(!v.available, v.available ? addText || undefined : soldOut);
      } else if (submit) {
        submit.disabled = !v.available;
        submit.setAttribute('aria-disabled', String(!v.available));
      }

      const payment = this.querySelector('.shopify-payment-button');
      if (payment) payment.hidden = !v.available;

      const status = this.querySelector('[data-rr-status]');
      if (status) status.textContent = v.available ? '' : soldOut;
    }

    updateQuantity(v) {
      const input = this.querySelector('quantity-input input.quantity__input');
      if (!input) return;
      const min = v.qmin || 1;
      input.min = min;
      input.step = v.qstep || 1;
      input.dataset.min = min;
      if (v.qmax) input.max = v.qmax;
      else input.removeAttribute('max');
      const cur = parseInt(input.value, 10);
      if (isNaN(cur) || cur < min) input.value = min;
      else if (v.qmax && cur > v.qmax) input.value = v.qmax;
    }

    showImage(imageId) {
      if (!imageId) return;
      const items = this.querySelectorAll('.rr-gallery__item');
      let found = false;
      items.forEach((it) => {
        if (it.dataset.imageId === String(imageId)) found = true;
      });
      if (!found) return;
      items.forEach((it) => {
        it.hidden = it.dataset.imageId !== String(imageId);
        if (it.hidden) {
          const vid = it.querySelector('video');
          if (vid && !vid.paused) vid.pause();
        }
      });
      this.querySelectorAll('[data-rr-thumb]').forEach((t) => {
        t.setAttribute('aria-current', String(t.dataset.imageId === String(imageId)));
      });
    }

    openLightbox(btn) {
      if (!this.lightbox || typeof this.lightbox.showModal !== 'function') return;
      const img = this.lightbox.querySelector('[data-rr-lightbox-img]');
      if (img) {
        img.src = btn.dataset.full;
        const inner = btn.querySelector('img');
        img.alt = inner ? inner.alt : '';
      }
      this.lightbox.showModal();
    }
  }
  customElements.define('rr-product-info', RRProductInfo);
}
